import { withApollo } from 'components/withApollo'
import { GetStaticPathsResult, NextPage } from 'next'
import createApolloClient from 'components/withApollo/createApolloClient'
import getStaticApolloProps from 'lib/getStaticApolloProps'
import { useRouter } from 'next/router'
import React from 'react'
import PageError from 'components/pages/PageError'
import Loading from 'components/Loading'
import PageMeta from 'components/PageMeta'
import PageLayout from 'components/pages/PageLayout'
import NotFoundPage from 'components/pages/NotFoundPage/NotFoundPage'
import ButtonLink from 'components/ui/ButtonLink'

import CtaSection from './CtaSection'
import ContentSections from './ContentSections'
import HeroSection from './HeroSection'
import { useBlogPostQuery } from './types/query'
import { BlogPostsPaths, BlogPostsPathsDocument } from './types/BlogPostsPaths'

type Props = {}

type Params = {
  seoName: string
  skipCache?: string
}

function BlogPostPage() {
  const router = useRouter()
  const { seoName } = router.query as Params

  const skipCache = Object.keys(router.query).includes('skipCache')

  const { data, error, loading } = useBlogPostQuery({
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
    variables: {
      seoName
    }
  })

  if (error) return <PageError>{error.message}</PageError>

  if (!data?.blogPost && loading) return <Loading className="w-full h-72" />

  const blogPost = data?.blogPost

  if (!blogPost || (!blogPost.published && !skipCache))
    return <NotFoundPage search={`${seoName || ''}`} />

  return (
    <>
      <PageMeta
        canonicalPath={blogPost.customerPath}
        description={blogPost.metaDescription}
        noIndex={!blogPost.published}
        title={`Best Food Trucks | ${blogPost.title}`}
      />
      <PageLayout fullWidth>
        <div className="w-full">
          <HeroSection blogPost={blogPost} />
          <ContentSections blogPost={blogPost} />
          <div className="max-w-7xl mx-auto pb-24 px-4 sm:px-6 lg:px-8">
            <ButtonLink
              className="underline"
              color="primary"
              href="/blog"
              size="large"
              variant="anchor"
            >
              Read other posts
            </ButtonLink>
          </div>
          <CtaSection />
        </div>
      </PageLayout>
    </>
  )
}

export default withApollo({ ssr: false })(BlogPostPage as NextPage)

export async function getStaticPaths(): Promise<GetStaticPathsResult> {
  const client = createApolloClient({}, null)

  const { data } = await client.query<BlogPostsPaths>({
    query: BlogPostsPathsDocument
  })

  const paths = data.customerPaths.blogPostsPaths.map((seoName) => ({
    params: { seoName }
  }))

  return {
    paths,
    fallback: 'blocking'
  }
}

export const getStaticProps = getStaticApolloProps<Props, Params>(
  BlogPostPage,
  {
    revalidate: 60 * 60 * 24 // 24 hours
  }
)
